import React from "react";
import { Button, Descriptions, Modal, Table } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { COMPLAINT_REASONS } from "../../../../../constants/DepartmentConstants";
import Loading from "../../../../../components/shared-components/Loading";

const ComplaintDetailsModal = ({ visible, onClose, complaintData, loading }) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={t("complaint_details")}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("close")}
        </Button>,
      ]}
      width={800}
    >
      {loading ? (
        <Loading />
      ) : complaintData ? (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t("complaint_id")}>{complaintData.id}</Descriptions.Item>
            <Descriptions.Item label={t("invoice_id")}>{complaintData.invoiceId || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("order_id")}>{complaintData.farmorderId || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("exact_invoice_id")}>{complaintData.exactInvoiceId || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("created_at")}>
              {complaintData.createdAt ? dayjs.unix(complaintData.createdAt).format("D MMM YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("created_by")}>{complaintData.createdBy || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("complaining_person")}>
              {complaintData.products?.[0]?.complainingPerson || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("company")}>{complaintData.company || "-"}</Descriptions.Item>
            {complaintData.description && (
              <Descriptions.Item label={t("general_description")} span={2}>
                {complaintData.description}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Images Section */}
          {complaintData.images && complaintData.images.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h6 style={{ marginBottom: 12 }}>{t("uploaded_photos")}</h6>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  padding: "12px",
                  backgroundColor: "#fafafa",
                  borderRadius: "6px",
                  border: "1px solid #e8e8e8",
                }}
              >
                {complaintData.images.map((imageUrl, index) => (
                  <a
                    key={index}
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      width: "80px",
                      height: "80px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      border: "2px solid #d9d9d9",
                      transition: "all 0.3s",
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#1890ff";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#d9d9d9";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Complaint ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: "rgba(0, 0, 0, 0.6)",
                        color: "white",
                        fontSize: "10px",
                        padding: "2px 4px",
                        textAlign: "center",
                      }}
                    >
                      <EyeOutlined /> Foto {index + 1}
                    </div>
                  </a>
                ))}
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "8px",
                  fontStyle: "italic",
                }}
              >
                {t("click_to_view_full")}
              </p>
            </div>
          )}

          {/* Products Table */}
          <div className="table-responsive">
            <Table
              showSorterTooltip={false}
              rowKey={(_, idx) => String(idx)}
              pagination={false}
              dataSource={complaintData.products || []}
              columns={[
                {
                  title: t("product"),
                  render: (_, rec) => rec.product?.productName || rec.product?.productId || "-",
                },
                {
                  title: t("quantity"),
                  dataIndex: "quantity",
                },
                {
                  title: t("complaint_reason"),
                  render: (_, rec) => {
                    const reason = COMPLAINT_REASONS.find((r) => r.id === rec.reason);
                    return reason ? t(reason.labelKey) : rec.reason;
                  },
                },
                {
                  title: t("custom_reason"),
                  dataIndex: "customReason",
                  render: (val) => val || "-",
                },
                {
                  title: t("complaining_person"),
                  dataIndex: "complainingPerson",
                  render: (val) => val || "-",
                },
              ]}
            />
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", color: "#999" }}>{t("no_data")}</div>
      )}
    </Modal>
  );
};

export default ComplaintDetailsModal;
